// This browser's Web Push subscription (HARDEN-pwa), behind one small interface so the settings
// toggle is tested without a service worker (`PushBrowser`). The server decides which inbox items
// are pushed (the Desktop column of the notification preferences, quiet hours); this only
// subscribes or unsubscribes the browser, with the server's VAPID public key.

export type PushSupport =
  | "supported"
  /** iPhone/iPad Safari outside an installed app: push needs Add to Home Screen first. */
  | "ios-install-first"
  | "unsupported";

export type PushBrowser = {
  support(): PushSupport;
  permission(): NotificationPermission | "unsupported";
  /** This browser's current subscription (JSON form), or null. */
  current(): Promise<PushSubscriptionJSON | null>;
  /** Asks permission when needed, then subscribes with the server's key. */
  subscribe(publicKey: string): Promise<PushSubscriptionJSON>;
  /** Ends the browser's subscription (the push service forgets the endpoint). */
  unsubscribe(): Promise<string | null>;
};

/** base64url → bytes (the `applicationServerKey` form). */
export function keyBytes(base64url: string): Uint8Array<ArrayBuffer> {
  const pad = "=".repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, "+").replace(/_/g, "/"));
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** SHA-256 of a string, hex (what `push.config` lists for each subscribed browser). */
export async function sha256Hex(text: string): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(text));
  return [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, "0")).join("");
}

const isIosLike = () =>
  /iPhone|iPad|iPod/.test(navigator.userAgent) ||
  (/Macintosh/.test(navigator.userAgent) && navigator.maxTouchPoints > 1);

/** The real browser. */
export const browserPush: PushBrowser = {
  support() {
    if (typeof window === "undefined") return "unsupported";
    const ok = "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
    if (ok) return "supported";
    const standalone = (navigator as Navigator & { standalone?: boolean }).standalone;
    return isIosLike() && !standalone ? "ios-install-first" : "unsupported";
  },
  permission() {
    return typeof window !== "undefined" && "Notification" in window
      ? window.Notification.permission
      : "unsupported";
  },
  async current() {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    return sub ? sub.toJSON() : null;
  },
  async subscribe(publicKey) {
    if (window.Notification.permission !== "granted") {
      const p = await window.Notification.requestPermission();
      if (p !== "granted") throw new Error("Notifications are not allowed in this browser.");
    }
    const reg = await navigator.serviceWorker.getRegistration();
    if (!reg) throw new Error("The app's service worker is not running yet. Reload and try again.");
    const sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: keyBytes(publicKey),
    });
    return sub.toJSON();
  },
  async unsubscribe() {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return null;
    const endpoint = sub.endpoint;
    await sub.unsubscribe();
    return endpoint;
  },
};
